<template>
    <div class="strategy-tab">
        <AonCoverLoading
            :loading="moat2ProductStore.productAlignmentLoading"
            :title="`Please hang tight!`"
            :message="loadingMessage"
        >
            <template #footer>
                <progress
                    v-if="moat2ProductStore.percentComplete !== 0"
                    :value="moat2ProductStore.percentComplete"
                    max="100"
                    class="mt-md progress-bar"
                />
            </template>
        </AonCoverLoading>
        <AonContainer
            v-if="
                !moat2ProductStore.productAlignmentLoading &&
                !moat2ProductStore.productAlignmentError
            "
        >
            <AonRow>
                <AonCoverLoading
                    :loading="loadingCoverageAndPatents"
                    title="Fetching Strategy Data"
                />
                <div v-if="!loadingCoverageAndPatents && !loadingStrategyByYear" class="w-100">
                    <AonCol class="grid-cols-12 pl-0 mt-md">
                        <div class="display-flex align-items-center justify-between w-100">
                            <div class="holder">
                                <p class="text-stone-600 mb-xxs">Strategy /</p>
                                <div class="display-flex align-items-center">
                                    <h4>{{ entityStore.entity.name }}</h4>
                                    <h4 class="capitalize-word ml-xs">
                                        {{ entityStore.strategyTabHighLevel }}
                                    </h4>
                                </div>
                            </div>
                            <MoatDropdownExpanded
                                v-if="entityStore.strategyTabHighLevel === 'innovation'"
                                :options="nodeFocusTabs"
                                v-model="entityStore.strategyTabNodeFilter"
                                @tab-clicked="onMinorTabClicked"
                            />
                        </div>
                    </AonCol>

                    <KeepAlive>
                        <div v-if="entityStore.strategyTabHighLevel === 'innovation'">
                            <AonCol class="grid-cols-12 p-0 mt-md">
                                <CoverageAndPatentsFocus
                                    :key="`capf-${entityStore.strategyTabNodeFilter}`"
                                    :product-data="fetchedProductData"
                                    :technology-data="fetchedTechnologyData"
                                />
                            </AonCol>
                            <AonCol class="grid-cols-12 p-0 mt-md">
                                <InnovationFilingsAndTimelineFocus
                                    :key="`ifatf-${entityStore.strategyTabNodeFilter}`"
                                    :product-data="fetchedProductData"
                                    :technology-data="fetchedTechnologyData"
                                    :data-by-year-product="fetchedDataByYearProduct"
                                    :data-by-year-technology="fetchedDataByYearTechnology"
                                />
                            </AonCol>
                        </div>
                    </KeepAlive>

                    <KeepAlive>
                        <div v-if="entityStore.strategyTabHighLevel === 'talent'">
                            <KeepAlive>
                                <AonCol class="grid-cols-12 p-0 mt-md">
                                    <TalentPath key="overview" />
                                </AonCol>
                            </KeepAlive>
                        </div>
                    </KeepAlive>
                </div>
            </AonRow>
        </AonContainer>
        <ErrorWithProductAlignment
            @get-product-alignment="onGetProductSegmentationData"
            @get-previous-run="onGetPreviousRunOrRequestNew(true)"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted } from 'vue'
import { useEntityStore, useMoat2ProductStore } from '@/stores'
import { getEntityProductStrategy, getEntityProductStrategyYearly } from '@/api/entities.js'
import { cloneDeep } from 'lodash-es'

import TalentPath from './TalentPath.vue'
import CoverageAndPatentsFocus from './innovationTabs/CoverageAndPatentsFocus.vue'
import InnovationFilingsAndTimelineFocus from './innovationTabs/InnovationFilingsAndTimelineFocus.vue'

import MoatDropdownExpanded from '../../../helpers/moatArmory/MoatDropdownExpanded.vue'
import ErrorWithProductAlignment from '@/components/helpers/ErrorWithProductAlignment.vue'

const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const logger = inject('logger')

const loadingCoverageAndPatents = ref(true)
const loadingStrategyByYear = ref(true)
const fetchedProductData = ref([])
const fetchedTechnologyData = ref([])
const fetchedDataByYearProduct = ref([])
const fetchedDataByYearTechnology = ref([])

const nodeFocusTabs = ref([
    {
        label: 'By Product Class',
        value: 'product',
        description: 'Moat’s classification of patents to product categories.',
    },
    {
        label: 'By Technology Class',
        value: 'technology',
        description:
            'A more detailed view showing Moat’s classification of patents to specific technology areas.',
    },
])

const loadingMessage = computed(() => {
    if (moat2ProductStore.needsNewAlignment) {
        return `We are aligning patents to the product and technology areas for ${entityStore.entity?.name}. This process will take 5-10 minutes if not cached in our system. Please feel free to check out the other tabs and this process will continue in the background.`
    } else {
        return `Loading Moat2Product for ${entityStore.entity?.name}`
    }
})

const innovationOverTimeDesc = computed(() => {
    return entityStore.strategyTabNodeFilter === 'product'
        ? `Visualize how ${entityStore.entity.name} Product investment strategy has evolved over time.`
        : `Visualize how ${entityStore.entity.name} Product and Technology investment strategies has evolved over time.`
})

onMounted(() => {
    if (entityStore.entity?.aon_entity_pk) {
        onGetStrategy()
        onGetStrategyByYear()
    }
})

watch(
    () => !entityStore.loadingEntity,
    (newValue, oldValue) => {
        onGetStrategy()
        onGetStrategyByYear()
    }
)

const onMinorTabClicked = (tab) => {
    entityStore.strategyTabNodeFilter = tab.value
}

const onGetPreviousRunOrRequestNew = async (getPreviousRun = false) => {
    await moat2ProductStore.getPreviousRunOrRequestNew(getPreviousRun)
}

const onGetProductSegmentationData = async (alignmentPk) => {
    await moat2ProductStore.getProductSegmentationData(alignmentPk)
}

// Strategy Tab Specific
const onGetStrategy = async () => {
    loadingCoverageAndPatents.value = true
    try {
        const { data } = await getEntityProductStrategy(entityStore.entity.aon_entity_pk)
        fetchedProductData.value = cloneDeep(data.filter((item) => item.ipNodeType === 'product'))
        fetchedTechnologyData.value = cloneDeep(
            data.filter(
                (item) =>
                    (item.ipNodeType === 'cluster' && item.displayName !== 'Other') ||
                    item.ipNodeType === 'concept'
            )
        )
    } catch (error) {
        logger.error(error)
    } finally {
        loadingCoverageAndPatents.value = false
    }
}

const onGetStrategyByYear = async () => {
    loadingStrategyByYear.value = true
    try {
        // Fetch both requests in parallel for better performance
        const [technologyData, productData] = await Promise.all([
            getEntityProductStrategyYearly(entityStore.entity.aon_entity_pk, null, {
                ipNodeType: 'concept',
            }),
            getEntityProductStrategyYearly(entityStore.entity.aon_entity_pk, null, {
                ipNodeType: 'product',
            }),
        ])
        fetchedDataByYearTechnology.value = technologyData.data
        fetchedDataByYearProduct.value = productData.data
    } catch (error) {
        logger.error(error)
    } finally {
        loadingStrategyByYear.value = false
    }
}
</script>

<style lang="scss" scoped>
.strategy-tab {
    position: relative;
    .light {
        border-color: var(--color-stone-200) !important;
    }
}
</style>
